import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight, ChevronLeft, Shield, Truck, RotateCcw, CreditCard, Lock, MapPin, Package, CheckCircle, Star, Check, Tag, Wallet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import ProductCard from '@/components/ProductCard';
import CheckoutModal from '@/components/CheckoutModal';
import { toast } from 'sonner';
import { toastBlue } from '@/utils/toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CartPage() {
  const { cart, updateQuantity, removeFromCart, cartTotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('BEMVINDO10');
  const [couponData, setCouponData] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [cep, setCep] = useState('');
  const [shippingCalculated, setShippingCalculated] = useState(false);
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const [suggestedProducts, setSuggestedProducts] = useState([]);
  const [productReviews, setProductReviews] = useState({});
  const [paymentMethod, setPaymentMethod] = useState('credit');
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [cashbackBalance, setCashbackBalance] = useState(0);
  const [useCashback, setUseCashback] = useState(false);
  const [cashbackAmount, setCashbackAmount] = useState('');

  // Fetch cashback balance
  useEffect(() => {
    if (!user) { setCashbackBalance(0); setUseCashback(false); return; }
    const fetchCashback = async () => {
      try {
        const res = await axios.get(`${API}/cashback/balance`);
        setCashbackBalance(res.data.balance || 0);
      } catch (e) { /* silent */ }
    };
    fetchCashback();
  }, [user]);

  // Fetch reviews for cart items
  useEffect(() => {
    const fetchReviews = async () => {
      const reviews = {};
      for (const item of cart) {
        try {
          const [reviewsRes, ratingRes] = await Promise.all([
            axios.get(`${API}/products/${item.product_id}/reviews`),
            axios.get(`${API}/products/${item.product_id}/rating`)
          ]);
          reviews[item.product_id] = {
            reviews: reviewsRes.data.slice(0, 3),
            rating: ratingRes.data
          };
        } catch (e) {
          // Silencioso
        }
      }
      setProductReviews(reviews);
    };
    if (cart.length > 0) {
      fetchReviews();
    }
  }, [cart]);

  // Fetch suggested products for upsell
  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const response = await axios.get(`${API}/products`, { params: { featured: true } });
        // Filter products not in cart
        const cartIds = cart.map(item => item.product_id);
        const suggestions = response.data.filter(p => !cartIds.includes(p.product_id)).slice(0, 3);
        setSuggestedProducts(suggestions);
      } catch (e) {
        console.error('Error fetching suggestions:', e);
      }
    };
    if (cart.length > 0) {
      fetchSuggestions();
    }
  }, [cart]);

  const [showFreteMsg, setShowFreteMsg] = useState(false);

  // Quantidade total de peças no carrinho (somando quantidades)
  const totalItemQty = cart.reduce((acc, it) => acc + (it.quantity || 1), 0);
  const FREE_SHIPPING_VALUE = 599;
  const FREE_SHIPPING_MIN_ITEMS = 2;
  const hasFreeShippingByQty = totalItemQty >= FREE_SHIPPING_MIN_ITEMS;
  const hasFreeShipping = cartTotal >= FREE_SHIPPING_VALUE || hasFreeShippingByQty;

  // 🛡️ Reseta o cálculo do frete sempre que o carrinho mudar (qtd, itens, valor).
  // Impede bug onde "frete grátis" obtido com 2 peças permanecia ao remover 1 peça, e vice-versa.
  const cartSignature = cart.map(it => `${it.product_id}|${it.size}|${it.color}|${it.quantity}`).join(',');
  const lastSignatureRef = useRef(cartSignature);
  useEffect(() => {
    if (lastSignatureRef.current !== cartSignature) {
      lastSignatureRef.current = cartSignature;
      // Limpa cálculo anterior — força recálculo correto
      setShippingCalculated(false);
      setShippingOptions([]);
      setSelectedShipping(null);
      setShowFreteMsg(false);
    }
  }, [cartSignature]);

  const handleCheckout = () => {
    if (!shippingCalculated && !hasFreeShipping) {
      setShowFreteMsg(true);
      return;
    }
    setShowFreteMsg(false);
    setCheckoutOpen(true);
  };

  // State for shipping info
  const [shippingCity, setShippingCity] = useState('');
  const [shippingState, setShippingState] = useState('');
  const [shippingError, setShippingError] = useState('');

  // Calculate shipping using real API
  const calculateShipping = async () => {
    const cepClean = cep.replace(/\D/g, '');
    
    if (cepClean.length !== 8) {
      toastBlue('Digite um CEP válido com 8 números');
      return;
    }
    
    setLoadingShipping(true);
    setShippingError('');
    
    try {
      const response = await axios.post(`${API}/shipping/calculate`, {
        cep: cepClean,
        cart_total: cartTotal,
        item_count: cart.reduce((sum, item) => sum + (item.quantity || 1), 0)
      });
      
      const data = response.data;
      
      if (!data.success) {
        setShippingError(data.error || 'Erro ao calcular frete');
        toastBlue(data.error || 'Erro ao calcular frete');
        setShippingCalculated(false);
        setLoadingShipping(false);
        return;
      }
      
      // Map API response to frontend format
      const options = data.options.map(opt => ({
        id: opt.id,
        name: opt.name,
        price: opt.price,
        days: opt.description,
        free: opt.price === 0
      }));
      
      setShippingOptions(options);
      setSelectedShipping(options[0]); // Default to first (cheapest)
      setShippingCalculated(true);
      setShippingCity(data.city || '');
      setShippingState(data.state || '');
      
      if (data.free_shipping_eligible) {
        toast.success('Parabéns! Você tem frete grátis! 🎉');
      } else {
        toast.success(`Frete calculado para ${data.city}/${data.state}`);
      }
      
    } catch (error) {
      console.error('Shipping error:', error);
      const errorMsg = error.response?.data?.error || 'Erro ao calcular frete. Tente novamente.';
      setShippingError(errorMsg);
      toastBlue(errorMsg);
      setShippingCalculated(false);
    } finally {
      setLoadingShipping(false);
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const response = await axios.post(`${API}/coupons/validate`, {
        code: couponCode.trim(),
        email: user?.email || ''
      });
      setCouponData(response.data);
      toast.success(`Cupom aplicado! ${(response.data.discount_percent * 100).toFixed(0)}% de desconto`);
    } catch (error) {
      setCouponError(error.response?.data?.detail || 'Cupom inválido');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponData(null);
    setCouponCode('');
    setCouponError('');
  };

  // Format CEP as user types
  const handleCepChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 8) value = value.slice(0, 8);
    if (value.length > 5) {
      value = value.slice(0, 5) + '-' + value.slice(5);
    }
    setCep(value);
    // Reset shipping when CEP changes
    if (shippingCalculated) {
      setShippingCalculated(false);
      setShippingOptions([]);
      setSelectedShipping(null);
    }
  };

  // Get actual shipping cost
  const shippingCost = shippingCalculated && selectedShipping 
    ? (selectedShipping.free ? 0 : selectedShipping.price) 
    : (hasFreeShipping ? 0 : null);
  
  const couponDiscount = couponData ? cartTotal * couponData.discount_percent : 0;
  const maxCashback = Math.min(cashbackBalance, Math.max(0, cartTotal + (shippingCost || 0) - couponDiscount));
  const typedCashback = parseFloat((cashbackAmount || '0').replace(',', '.')) || 0;
  const cashbackApplied = useCashback ? Math.min(Math.max(0, typedCashback), maxCashback) : 0;
  const finalTotal = cartTotal + (shippingCost || 0) - couponDiscount - cashbackApplied;

  const handleCashbackInput = (val) => {
    setCashbackAmount(val.replace(/[^0-9.,]/g, ''));
  };
  const toggleCashback = () => {
    const next = !useCashback;
    setUseCashback(next);
    if (next) setCashbackAmount(maxCashback.toFixed(2).replace('.', ','));
  };
  const pixDiscount = finalTotal * 0.05;
  const amountToFreeShipping = FREE_SHIPPING_VALUE - cartTotal;
  
  // Brinde acima de R$1.200
  const hasGift = cartTotal >= 1200;
  const amountToGift = 1200 - cartTotal;

  const pixFinalTotal = finalTotal * 0.95;

  // Oculta aviso de frete assim que o frete for calculado
  useEffect(() => {
    if (shippingCalculated) setShowFreteMsg(false);
  }, [shippingCalculated]);

  if (cart.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4" data-testid="empty-cart">
        <ShoppingBag className="h-16 w-16 text-gray-300 mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2 font-['Playfair_Display']">
          Seu carrinho está vazio
        </h2>
        <p className="text-gray-600 mb-6">Explore nossa coleção e encontre peças incríveis!</p>
        <Link to="/produtos">
          <Button className="bg-[#0a0a0a] hover:bg-gray-800 rounded-full px-8">
            Ver Produtos
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F8F8]" data-testid="cart-page">
      <CheckoutModal
        isOpen={checkoutOpen}
        onClose={() => setCheckoutOpen(false)}
        finalAmount={finalTotal}
        pixAmount={pixFinalTotal}
        paymentMethod={paymentMethod}
        cartItems={cart}
        couponCode={couponData?.code || null}
        couponDiscount={couponData ? cartTotal * couponData.discount_percent : 0}
        subtotal={cartTotal}
        cashbackUsed={cashbackApplied}
      />
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <Link 
            to="/produtos" 
            className="inline-flex items-center text-sm text-gray-600 hover:text-[#0a0a0a] mb-4 transition-colors"
          >
            <ChevronLeft className="h-4 w-4 mr-1" />
            ← Continuar comprando
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Playfair_Display']">
            Meu Carrinho
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {cart.length} {cart.length === 1 ? 'item' : 'itens'} no carrinho
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart items */}
          <div className="lg:col-span-2 space-y-4 pb-8 md:pb-0">
            {/* Free shipping progress bar — focada na promoção "2+ peças" */}
            {!hasFreeShipping && (
              <div className="bg-neutral-100 border border-neutral-200 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 bg-[#0a0a0a] rounded-full flex items-center justify-center flex-shrink-0">
                    <Truck className="h-3.5 w-3.5 text-white" />
                  </div>
                  <p className="text-xs font-semibold text-[#0a0a0a] flex-1">
                    Adicione <span className="text-[#0a0a0a] font-bold">+{Math.max(FREE_SHIPPING_MIN_ITEMS - totalItemQty, 1)} {Math.max(FREE_SHIPPING_MIN_ITEMS - totalItemQty, 1) === 1 ? 'peça' : 'peças'}</span> para garantir <span className="font-bold">FRETE GRÁTIS!</span>
                  </p>
                </div>
                <div className="w-full bg-neutral-300 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="bg-[#0a0a0a] h-1.5 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${Math.min((totalItemQty / FREE_SHIPPING_MIN_ITEMS) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-neutral-400 text-right mt-1"><span className="text-emerald-600">Frete grátis em pedidos com 2+ peças</span></p>
              </div>
            )}
            
            {/* Free shipping achieved */}
            {hasFreeShipping && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-green-800">Parabéns! Você ganhou FRETE GRÁTIS 🎉</p>
                  <p className="text-xs text-green-700">
                    Promoção 2+ peças aplicada — seu pedido será enviado sem custo
                  </p>
                </div>
              </div>
            )}
            
            {/* Brinde acima de R$1.200 - Progress bar */}
            {amountToGift > 0 && (
              <div className="bg-amber-50/60 border border-amber-200/70 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 bg-amber-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-sm">🎁</span>
                  </div>
                  <p className="text-xs font-semibold text-amber-900 flex-1">
                    Faltam <span className="font-bold">R$ {amountToGift.toFixed(2).replace('.', ',')}</span> para ganhar um BRINDE!
                  </p>
                </div>
                <div className="w-full bg-amber-200/60 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="bg-amber-600 h-1.5 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${Math.min((cartTotal / 1200) * 100, 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-amber-400 text-right mt-1">Brinde em compras acima de R$ 1.200,00</p>
              </div>
            )}

            {/* Brinde conquistado */}
            {hasGift && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-xl">🎁</span>
                </div>
                <div>
                  <p className="font-semibold text-amber-700">Você ganhou um BRINDE! 🎉</p>
                  <p className="text-xs text-amber-600">Compras acima de R$1.200 ganham um brinde especial</p>
                </div>
              </div>
            )}

            {/* Cart items list with zebra stripes */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {cart.map((item, index) => (
                <div 
                  key={`${item.product_id}-${item.size}-${item.color}`}
                  className={`p-4 md:p-6 ${index % 2 === 1 ? 'bg-gray-50/50' : 'bg-white'} ${index !== cart.length - 1 ? 'border-b border-gray-100' : ''}`}
                  data-testid={`cart-item-${item.product_id}`}
                >
                  <div className="flex gap-4">
                    {/* Image */}
                    <Link 
                      to={`/produto/${item.product_id}`} 
                      className="w-24 h-32 md:w-28 md:h-36 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0 shadow-sm"
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </Link>

                    {/* Details */}
                    <div className="flex-1 min-w-0 flex flex-col justify-between">
                      <div>
                        <div className="flex justify-between items-start gap-2">
                          <Link to={`/produto/${item.product_id}`}>
                            <h3 className="font-semibold text-gray-900 text-sm md:text-base leading-tight hover:text-[#0a0a0a] transition-colors line-clamp-2">
                              {item.name}
                            </h3>
                          </Link>
                          
                          {/* Remove button with tooltip */}
                          <button
                            onClick={() => removeFromCart(item.product_id, item.size, item.color)}
                            className="text-gray-400 hover:text-red-500 transition-colors p-1.5 hover:bg-red-50 rounded-full"
                            data-testid={`remove-${item.product_id}`}
                            title="Remover item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        
                        {/* Product details */}
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                          <span className="inline-flex items-center">
                            Tamanho: <span className="font-medium text-gray-700 ml-1">{item.size}</span>
                          </span>
                          <span className="inline-flex items-center">
                            Cor: <span className="font-medium text-gray-700 ml-1">{item.color}</span>
                          </span>
                        </div>
                      </div>

                      {/* Price & Quantity */}
                      <div className="mt-4 flex items-end justify-between">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateQuantity(item.product_id, item.size, item.color, item.quantity - 1)}
                            className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-lg hover:border-[#0a0a0a] hover:bg-[#0a0a0a]/5 transition-colors"
                            disabled={item.quantity <= 1}
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-10 text-center font-semibold text-gray-900">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.product_id, item.size, item.color, item.quantity + 1)}
                            className="w-8 h-8 flex items-center justify-center border border-gray-200 rounded-lg hover:border-[#0a0a0a] hover:bg-[#0a0a0a]/5 transition-colors"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        
                        <div className="text-right">
                          {item.original_price && (
                            <p className="text-xs text-gray-400 line-through">
                              R$ {(item.original_price * item.quantity).toFixed(2).replace('.', ',')}
                            </p>
                          )}
                          <p className="font-bold text-[#0a0a0a] text-lg">
                            R$ {(item.price * item.quantity).toFixed(2).replace('.', ',')}
                          </p>
                        </div>
                      </div>

                      {/* Avaliações do produto no carrinho */}
                      {productReviews[item.product_id] && productReviews[item.product_id].rating?.total_reviews > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <div className="flex">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`h-3 w-3 ${
                                    star <= Math.round(productReviews[item.product_id].rating.average_rating)
                                      ? 'text-amber-400 fill-amber-400'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                            </div>
                            <span className="text-xs text-gray-600">
                              {productReviews[item.product_id].rating.average_rating.toFixed(1)} ({productReviews[item.product_id].rating.total_reviews} avaliações)
                            </span>
                          </div>
                          {productReviews[item.product_id].reviews?.slice(0, 2).map((review, idx) => (
                            <div key={idx} className="text-xs text-gray-500 mb-1 flex items-start gap-1">
                              <Check className="h-3 w-3 text-green-500 flex-shrink-0 mt-0.5" />
                              <span>&ldquo;{review.comment}&rdquo; - <span className="font-medium">{review.user_name}</span></span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Shipping calculator */}
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="h-5 w-5 text-[#0a0a0a]" />
                Calcular Frete e Prazo
              </h3>
              
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="00000-000"
                  value={cep}
                  onChange={handleCepChange}
                  maxLength={9}
                  className="flex-1"
                  data-testid="cep-input"
                />
                <Button
                  onClick={calculateShipping}
                  disabled={loadingShipping || cep.replace(/\D/g, '').length < 8}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-900 px-4 disabled:bg-gray-100 disabled:text-gray-500"
                  data-testid="calc-shipping-btn"
                >
                  {loadingShipping ? 'Calculando...' : 'Calcular'}
                </Button>
              </div>
              
              <a 
                href="https://buscacepinter.correios.com.br/app/endereco/index.php" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-block mt-2 text-xs text-gray-500 hover:text-gray-700 hover:underline"
              >
                Não sei meu CEP
              </a>

              {/* Shipping error */}
              {shippingError && (
                <p className="mt-2 text-sm text-blue-600">{shippingError}</p>
              )}

              {/* Shipping destination info */}
              {shippingCalculated && shippingCity && (
                <p className="mt-2 text-sm text-gray-600">
                  Entrega para: <span className="font-medium">{shippingCity}/{shippingState}</span>
                </p>
              )}
              
              {/* Shipping options */}
              {shippingCalculated && shippingOptions.length > 0 && (
                <div className="mt-4 space-y-2">
                  {shippingOptions.map((option) => (
                    <label
                      key={option.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                        selectedShipping?.id === option.id 
                          ? 'border-[#0a0a0a] bg-[#0a0a0a]/5' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="shipping"
                        checked={selectedShipping?.id === option.id}
                        onChange={() => setSelectedShipping(option)}
                        className="w-4 h-4 text-[#0a0a0a] focus:ring-[#0a0a0a]"
                      />
                      <Package className="h-5 w-5 text-gray-400" />
                      <div className="flex-1">
                        <p className="font-medium text-sm text-gray-900">{option.name}</p>
                        <p className="text-xs text-gray-500">{option.days}</p>
                      </div>
                      <div className="text-right">
                        {option.free ? (
                          <span className="text-green-700 font-semibold text-sm">Grátis</span>
                        ) : (
                          <span className="font-semibold text-sm text-gray-900">
                            R$ {option.price.toFixed(2).replace('.', ',')}
                          </span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Resumo do Pedido - Mobile Only (completo com formas de pagamento) */}
            <div className="lg:hidden bg-white rounded-xl shadow-sm p-5" data-testid="cart-summary-mobile">
              <div className="flex items-center justify-between mb-5 pb-4 border-b">
                <h2 className="text-lg font-bold text-gray-900">
                  Resumo do Pedido
                </h2>
                {/* Selo de segurança - Mobile */}
                <div className="relative group">
                  <div className="flex items-center gap-1 text-gray-400 text-xs">
                    <Lock className="h-3 w-3" />
                    <span>100% Seguro</span>
                  </div>
                  {/* Tooltip no hover */}
                  <div className="absolute right-0 top-6 w-56 bg-gray-900 text-white text-xs p-3 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-lg pointer-events-none">
                    <div className="flex items-start gap-2">
                      <Shield className="h-4 w-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold mb-1">Compra Protegida</p>
                        <p className="text-gray-300 leading-relaxed">Seus dados estão seguros. Utilizamos criptografia SSL.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                {/* Subtotal */}
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal (<span className="text-[#0a0a0a] font-semibold">{cart.reduce((sum, i) => sum + i.quantity, 0)}</span> {cart.reduce((sum, i) => sum + i.quantity, 0) === 1 ? 'item' : 'itens'})</span>
                  <span className="font-medium text-gray-900">R$ {cartTotal.toFixed(2).replace('.', ',')}</span>
                </div>
                
                {/* Shipping */}
                <div className="flex justify-between text-gray-600">
                  <span className="flex items-center gap-1.5">
                    <Truck className="h-4 w-4" />
                    Frete
                  </span>
                  {shippingCost === null ? (
                    <span className="text-sm text-gray-500 italic">Calcular acima</span>
                  ) : shippingCost === 0 ? (
                    <span className="text-green-700 font-semibold">Grátis</span>
                  ) : (
                    <span className="font-medium text-gray-900">R$ {shippingCost.toFixed(2).replace('.', ',')}</span>
                  )}
                </div>

                {/* Cupom - Mobile */}
                <div>
                  {!couponData ? (
                    <div>
                      <div className="flex gap-2">
                        <div className="relative flex-1 overflow-hidden rounded-md">
                          <Input
                            type="text"
                            placeholder="Cupom de desconto"
                            value={couponCode}
                            onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                            className="w-full text-sm h-9 uppercase tracking-widest font-semibold text-green-800 border-green-300 bg-green-50/50"
                            data-testid="coupon-input-mobile"
                            onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                          />
                          <span className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden rounded-md">
                            <span style={{ position:'absolute', top:0, left:0, width:'50%', height:'100%', background:'linear-gradient(90deg, transparent 0%, rgba(22,101,52,0.06) 30%, rgba(22,101,52,0.18) 50%, rgba(22,101,52,0.06) 70%, transparent 100%)', animation:'shimmer-slide 2.8s ease-in-out infinite' }} />
                          </span>
                        </div>
                        <Button
                          onClick={applyCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                          className="bg-gray-900 hover:bg-gray-700 text-white px-3 h-9 text-xs shrink-0"
                          data-testid="apply-coupon-btn-mobile"
                        >
                          {couponLoading ? '...' : 'Aplicar'}
                        </Button>
                      </div>
                      {couponError && <p className="text-blue-600 text-xs mt-1">{couponError}</p>}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Tag className="h-3.5 w-3.5 text-green-700" />
                        <div>
                          <p className="text-xs font-bold text-green-800">{couponData.code}</p>
                          <p className="text-[10px] text-green-600">{couponData.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-green-700">-R$ {couponDiscount.toFixed(2).replace('.', ',')}</p>
                        <button onClick={removeCoupon} className="text-[10px] text-gray-400 hover:text-red-500">Remover</button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Cashback - Mobile */}
                {user && cashbackBalance > 0 && (
                  <div data-testid="cashback-section-mobile">
                    <button
                      onClick={toggleCashback}
                      className={`w-full flex items-center justify-between rounded-lg px-3 py-2.5 border-2 transition-all ${
                        useCashback
                          ? 'border-emerald-600 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300 bg-white'
                      }`}
                      data-testid="cashback-toggle-mobile"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                          useCashback ? 'bg-emerald-600 border-emerald-600' : 'border-gray-300'
                        }`}>
                          {useCashback && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <Wallet className="h-4 w-4 text-emerald-600" />
                        <span className="text-xs font-semibold text-gray-800">Usar Cashback</span>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-emerald-700">R$ {cashbackBalance.toFixed(2).replace('.', ',')}</p>
                        <p className="text-[9px] text-gray-500">disponível</p>
                      </div>
                    </button>
                    {useCashback && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 flex-1 border-2 border-emerald-200 rounded-lg px-2 py-1.5 bg-white focus-within:border-emerald-500 transition-colors">
                            <span className="text-xs font-semibold text-gray-500">R$</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={cashbackAmount}
                              onChange={(e) => handleCashbackInput(e.target.value)}
                              placeholder="0,00"
                              className="w-full text-xs font-bold text-gray-800 outline-none bg-transparent"
                              data-testid="cashback-amount-input-mobile"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => setCashbackAmount(maxCashback.toFixed(2).replace('.', ','))}
                            className="text-[10px] font-bold text-emerald-700 underline whitespace-nowrap"
                            data-testid="cashback-use-max-mobile"
                          >
                            Usar máx
                          </button>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-1">Máx aplicável: R$ {maxCashback.toFixed(2).replace('.', ',')}</p>
                      </div>
                    )}
                    {useCashback && cashbackApplied > 0 && (
                      <p className="text-xs text-emerald-700 font-semibold mt-1 text-right">
                        -R$ {cashbackApplied.toFixed(2).replace('.', ',')} aplicado
                      </p>
                    )}
                  </div>
                )}

                {/* Total */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between items-baseline">
                    <span className="text-lg font-bold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-[#0a0a0a]">
                      R$ {finalTotal.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1 text-right">
                    ou 12x de <span className="font-medium">R$ {((finalTotal * 1.2211) / 12).toFixed(2).replace('.', ',')}</span>
                  </p>
                </div>
              </div>

              {/* Aviso frete - Mobile */}
              {showFreteMsg && (
                <div className="flex items-start gap-2 bg-blue-50 border border-blue-500 rounded-lg p-3 mt-3">
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shrink-0 mt-0.5"><span className="text-white text-xs font-bold leading-none">i</span></div>
                  <p className="text-sm text-blue-800">Calcule o frete antes de finalizar a compra.</p>
                </div>
              )}

              {/* Cashback a ganhar - Mobile */}
              {user && finalTotal > 0 && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 mt-3" data-testid="cashback-earn-preview-mobile">
                  <Wallet className="h-4 w-4 text-emerald-600 shrink-0" />
                  <p className="text-xs text-emerald-800">
                    Você vai ganhar <span className="font-bold text-emerald-700">R$ {(finalTotal * 0.10).toFixed(2).replace('.', ',')}</span> de cashback nesta compra!
                  </p>
                </div>
              )}

              {/* CTA Button Mobile - Verde */}
              <Button
                onClick={handleCheckout}
                className="w-full bg-green-800 hover:bg-green-900 text-white rounded-lg py-5 text-base font-semibold mt-4 shadow-lg"
                data-testid="checkout-btn-mobile"
              >
                <Lock className="mr-2 h-4 w-4" />
                FINALIZAR COMPRA
              </Button>

              <Link to="/produtos" className="block mt-2">
                <Button
                  className="w-full bg-[#f7f7f7] hover:bg-[#0a0a0a] text-black hover:text-white border border-black rounded-lg py-5 text-sm font-medium transition-colors duration-200"
                  data-testid="continue-shopping-btn-mobile"
                >
                  Continuar Comprando
                </Button>
              </Link>

              {/* Payment method selector - Mobile */}
              <div className="mt-4 pt-4 border-t">
                <p className="text-xs text-gray-500 text-center mb-3 font-medium uppercase tracking-wider">
                  Forma de pagamento
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {/* Credit Card option */}
                  <button
                    onClick={() => setPaymentMethod('credit')}
                    className={`p-2.5 rounded-lg border-2 transition-all text-left ${
                      paymentMethod === 'credit'
                        ? 'border-[#0a0a0a] bg-[#0a0a0a]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === 'credit' ? 'border-[#0a0a0a]' : 'border-gray-300'
                      }`}>
                        {paymentMethod === 'credit' && (
                          <div className="w-1.5 h-1.5 rounded-full bg-[#0a0a0a]" />
                        )}
                      </div>
                      <CreditCard className="h-3.5 w-3.5 text-gray-700" />
                      <span className="text-[11px] font-semibold text-gray-800">Cartão</span>
                    </div>
                    {paymentMethod === 'credit' && (
                      <div className="mt-1.5 pl-5">
                        <img 
                          src="/payment_methods.jpg" 
                          alt="Visa, Master, Elo, Amex, Hiper"
                          className="h-3.5 w-auto object-contain"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </button>
                  
                  {/* PIX option */}
                  <button
                    onClick={() => setPaymentMethod('pix')}
                    className={`p-2.5 rounded-lg border-2 transition-all text-left ${
                      paymentMethod === 'pix'
                        ? 'border-green-800 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      <div className={`w-3.5 h-3.5 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === 'pix' ? 'border-green-800' : 'border-gray-300'
                      }`}>
                        {paymentMethod === 'pix' && (
                          <div className="w-1.5 h-1.5 rounded-full bg-green-800" />
                        )}
                      </div>
                      <svg viewBox="0 0 512 512" className="h-3.5 w-3.5">
                        <path fill="#32BCAD" d="M112.6 311.1l83.1 83.1c14.4 14.4 33.6 22.4 53.9 22.4h.2c20.3 0 39.5-7.9 53.9-22.4l83.1-83.1c14.4-14.4 22.4-33.6 22.4-53.9v-.2c0-20.3-7.9-39.5-22.4-53.9l-83.1-83.1c-14.4-14.4-33.6-22.4-53.9-22.4h-.2c-20.3 0-39.5 7.9-53.9 22.4l-83.1 83.1c-14.4 14.4-22.4 33.6-22.4 53.9v.2c0 20.3 7.9 39.5 22.4 53.9z"/>
                      </svg>
                      <span className="text-[11px] font-semibold text-gray-800">PIX</span>
                    </div>
                    <p className="text-[9px] text-green-800 font-semibold mt-1 pl-5">5% de desconto</p>
                  </button>
                </div>

                {/* Payment details based on selection - Mobile */}
                {paymentMethod === 'credit' && (
                  <div className="mt-2.5 text-center bg-gray-50 rounded-lg p-2">
                    <p className="text-xs text-gray-600">
                      até <span className="font-bold text-gray-900">12x</span> de <span className="font-bold text-gray-900">R$ {((finalTotal * 1.2211) / 12).toFixed(2).replace('.', ',')}</span>
                    </p>
                  </div>
                )}
                {paymentMethod === 'pix' && (
                  <div className="mt-2.5 bg-gradient-to-r from-green-800/10 to-green-900/10 border border-green-200 rounded-lg p-2 text-center">
                    <p className="text-green-800 text-base font-bold">
                      R$ {(finalTotal * 0.95).toFixed(2).replace('.', ',')}
                    </p>
                    <p className="text-[9px] text-green-700">
                      Economize <strong>R$ {pixDiscount.toFixed(2).replace('.', ',')}</strong> (5% off)
                    </p>
                  </div>
                )}
              </div>

              {/* Security message - Mobile */}
              <div className="flex flex-col items-center gap-1 mt-3">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Shield className="h-4 w-4 text-green-700" />
                  <span>Pagamento 100% seguro via <strong>Mercado Pago</strong></span>
                </div>
                <a href="/sobre#privacidade" className="text-[10px] text-black font-semibold underline hover:no-underline">Como protegemos sua compra?</a>
              </div>

              {/* Trust badges mobile */}
              <div className="mt-4 pt-4 border-t">
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-2">
                    <div className="w-7 h-7 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Lock className="h-3 w-3 text-white" />
                    </div>
                    <div>
                      <p className="text-[9px] font-bold text-green-800">SSL 256-bit</p>
                      <p className="text-[8px] text-green-700">Criptografia</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-2">
                    <div className="w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Shield className="h-3 w-3 text-white" />
                    </div>
                    <div>
                      <p className="text-[9px] font-bold text-blue-800">Protegida</p>
                      <p className="text-[8px] text-blue-600">Garantia</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap justify-center gap-2">
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-[10px] text-gray-600">
                    <CheckCircle className="h-3 w-3 text-green-700" />
                    Site Verificado
                  </span>
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 rounded-full text-[10px] text-gray-600">
                    <RotateCcw className="h-3 w-3 text-[#0a0a0a]" />
                    Troca grátis até 7 dias
                  </span>
                </div>
              </div>
            </div>

            {/* Upsell - Suggested products */}
            {suggestedProducts.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Complete seu pedido</h3>
                <div className="grid grid-cols-3 gap-3">
                  {suggestedProducts.map((product) => (
                    <Link 
                      key={product.product_id}
                      to={`/produto/${product.product_id}`}
                      className="group"
                    >
                      <div className="aspect-[3/4] bg-gray-100 rounded-lg overflow-hidden mb-2">
                        <img 
                          src={product.images[0]} 
                          alt={product.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        />
                      </div>
                      <p className="text-xs text-gray-900 line-clamp-1 group-hover:text-[#0a0a0a]">
                        {product.name}
                      </p>
                      <p className="text-xs font-semibold text-[#0a0a0a]">
                        R$ {product.price.toFixed(2).replace('.', ',')}
                      </p>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Summary - Sticky sidebar - APENAS DESKTOP */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm p-6 sticky top-24" data-testid="cart-summary">
              <div className="flex items-center justify-between mb-5 pb-4 border-b">
                <h2 className="text-lg font-bold text-gray-900">
                  Resumo do Pedido
                </h2>
                {/* Selo de segurança */}
                <div className="relative group">
                  <div className="flex items-center gap-1 text-gray-400 text-xs cursor-pointer">
                    <Lock className="h-3 w-3" />
                    <span>100% Seguro</span>
                  </div>
                  {/* Tooltip no hover */}
                  <div className="absolute right-0 top-6 w-64 bg-gray-900 text-white text-xs p-3 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-lg">
                    <div className="flex items-start gap-2">
                      <Shield className="h-4 w-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold mb-1">Compra Protegida</p>
                        <p className="text-gray-300 leading-relaxed">Seus dados estão seguros. Utilizamos criptografia SSL e não armazenamos dados do cartão.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                {/* Subtotal */}
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal ({cart.reduce((sum, i) => sum + i.quantity, 0)} {cart.reduce((sum, i) => sum + i.quantity, 0) === 1 ? 'item' : 'itens'})</span>
                  <span className="font-medium text-gray-900">R$ {cartTotal.toFixed(2).replace('.', ',')}</span>
                </div>
                
                {/* Shipping */}
                <div className="flex justify-between text-gray-600">
                  <span className="flex items-center gap-1.5">
                    <Truck className="h-4 w-4" />
                    Frete
                  </span>
                  {shippingCost === null ? (
                    <span className="text-sm text-gray-500 italic">Calcular acima</span>
                  ) : shippingCost === 0 ? (
                    <span className="text-green-700 font-semibold">Grátis</span>
                  ) : (
                    <span className="font-medium text-gray-900">R$ {shippingCost.toFixed(2).replace('.', ',')}</span>
                  )}
                </div>
                
                {/* Delivery estimate */}
                {shippingCalculated && selectedShipping && (
                  <div className="flex justify-between text-gray-600 text-sm">
                    <span>Prazo de entrega</span>
                    <span className="text-gray-900">{selectedShipping.days}</span>
                  </div>
                )}

                {/* Cupom */}
                <div>
                  {!couponData ? (
                    <div>
                      <div className="flex gap-2">
                        <div className="relative flex-1 overflow-hidden rounded-md">
                          <Input
                            type="text"
                            placeholder="Cupom de desconto"
                            value={couponCode}
                            onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(''); }}
                            className="w-full text-sm h-9 uppercase tracking-widest font-semibold text-green-800 border-green-300 bg-green-50/50"
                            data-testid="coupon-input"
                            onKeyDown={(e) => e.key === 'Enter' && applyCoupon()}
                          />
                          <span className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden rounded-md">
                            <span style={{ position:'absolute', top:0, left:0, width:'50%', height:'100%', background:'linear-gradient(90deg, transparent 0%, rgba(22,101,52,0.06) 30%, rgba(22,101,52,0.18) 50%, rgba(22,101,52,0.06) 70%, transparent 100%)', animation:'shimmer-slide 2.8s ease-in-out infinite' }} />
                          </span>
                        </div>
                        <Button
                          onClick={applyCoupon}
                          disabled={couponLoading || !couponCode.trim()}
                          className="bg-gray-900 hover:bg-gray-700 text-white px-3 h-9 text-xs shrink-0"
                          data-testid="apply-coupon-btn"
                        >
                          {couponLoading ? '...' : 'Aplicar'}
                        </Button>
                      </div>
                      {couponError && <p className="text-blue-600 text-xs mt-1">{couponError}</p>}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                      <div className="flex items-center gap-2">
                        <Tag className="h-3.5 w-3.5 text-green-700" />
                        <div>
                          <p className="text-xs font-bold text-green-800">{couponData.code}</p>
                          <p className="text-[10px] text-green-600">{couponData.description}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold text-green-700">-R$ {couponDiscount.toFixed(2).replace('.', ',')}</p>
                        <button onClick={removeCoupon} className="text-[10px] text-gray-400 hover:text-red-500">Remover</button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Cashback - Desktop */}
                {user && cashbackBalance > 0 && (
                  <div data-testid="cashback-section-desktop">
                    <button
                      onClick={toggleCashback}
                      className={`w-full flex items-center justify-between rounded-lg px-3 py-2.5 border-2 transition-all ${
                        useCashback
                          ? 'border-emerald-600 bg-emerald-50'
                          : 'border-gray-200 hover:border-emerald-300 bg-white'
                      }`}
                      data-testid="cashback-toggle-desktop"
                    >
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                          useCashback ? 'bg-emerald-600 border-emerald-600' : 'border-gray-300'
                        }`}>
                          {useCashback && <Check className="h-3 w-3 text-white" />}
                        </div>
                        <Wallet className="h-4 w-4 text-emerald-600" />
                        <span className="text-xs font-semibold text-gray-800">Usar Cashback</span>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-emerald-700">R$ {cashbackBalance.toFixed(2).replace('.', ',')}</p>
                        <p className="text-[9px] text-gray-500">disponível</p>
                      </div>
                    </button>
                    {useCashback && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center gap-1 flex-1 border-2 border-emerald-200 rounded-lg px-2 py-1.5 bg-white focus-within:border-emerald-500 transition-colors">
                            <span className="text-xs font-semibold text-gray-500">R$</span>
                            <input
                              type="text"
                              inputMode="decimal"
                              value={cashbackAmount}
                              onChange={(e) => handleCashbackInput(e.target.value)}
                              placeholder="0,00"
                              className="w-full text-xs font-bold text-gray-800 outline-none bg-transparent"
                              data-testid="cashback-amount-input-desktop"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => setCashbackAmount(maxCashback.toFixed(2).replace('.', ','))}
                            className="text-[10px] font-bold text-emerald-700 underline whitespace-nowrap"
                            data-testid="cashback-use-max-desktop"
                          >
                            Usar máx
                          </button>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-1">Máx aplicável: R$ {maxCashback.toFixed(2).replace('.', ',')}</p>
                      </div>
                    )}
                    {useCashback && cashbackApplied > 0 && (
                      <p className="text-xs text-emerald-700 font-semibold mt-1 text-right">
                        -R$ {cashbackApplied.toFixed(2).replace('.', ',')} aplicado
                      </p>
                    )}
                  </div>
                )}

                {/* Total */}
                <div className="border-t pt-4 mt-4">
                  <div className="flex justify-between items-baseline">
                    <span className="text-lg font-bold text-gray-900">Total</span>
                    <span className="text-2xl font-bold text-[#0a0a0a]">
                      R$ {finalTotal.toFixed(2).replace('.', ',')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 mt-1 text-right">
                    ou 12x de <span className="font-medium">R$ {((finalTotal * 1.2211) / 12).toFixed(2).replace('.', ',')}</span>
                  </p>
                </div>
              </div>

              {/* Aviso frete - Desktop */}
              {showFreteMsg && (
                <div className="flex items-start gap-2 bg-blue-50 border border-blue-500 rounded-lg p-3 mt-4">
                  <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center shrink-0 mt-0.5"><span className="text-white text-xs font-bold leading-none">i</span></div>
                  <p className="text-sm text-blue-800">Calcule o frete antes de finalizar a compra.</p>
                </div>
              )}

              {/* Cashback a ganhar - Desktop */}
              {user && finalTotal > 0 && (
                <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5 mt-4" data-testid="cashback-earn-preview-desktop">
                  <Wallet className="h-4 w-4 text-emerald-600 shrink-0" />
                  <p className="text-xs text-emerald-800">
                    Você vai ganhar <span className="font-bold text-emerald-700">R$ {(finalTotal * 0.10).toFixed(2).replace('.', ',')}</span> de cashback nesta compra!
                  </p>
                </div>
              )}

              {/* CTA Button - Verde com animação */}
              <Button
                onClick={handleCheckout}
                className="w-full bg-green-800 hover:bg-green-900 active:scale-[0.98] text-white rounded-lg py-6 text-base font-semibold mt-6 shadow-lg shadow-green-800/25 hover:shadow-green-800/40 transition-all duration-200"
                data-testid="checkout-btn"
              >
                <Lock className="mr-2 h-4 w-4" />
                FINALIZAR COMPRA
              </Button>

              <Link to="/produtos" className="block mt-2">
                <Button
                  className="w-full bg-[#f7f7f7] hover:bg-[#0a0a0a] text-black hover:text-white border border-black rounded-lg py-5 text-sm font-medium transition-colors duration-200"
                  data-testid="continue-shopping-btn"
                >
                  Continuar Comprando
                </Button>
              </Link>

              {/* Payment method selector */}
              <div className="mt-5 pt-5 border-t">
                <p className="text-xs text-gray-500 text-center mb-3 font-medium uppercase tracking-wider">
                  Forma de pagamento
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {/* Credit Card option */}
                  <button
                    onClick={() => setPaymentMethod('credit')}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      paymentMethod === 'credit'
                        ? 'border-[#0a0a0a] bg-[#0a0a0a]/5'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === 'credit' ? 'border-[#0a0a0a]' : 'border-gray-300'
                      }`}>
                        {paymentMethod === 'credit' && (
                          <div className="w-2 h-2 rounded-full bg-[#0a0a0a]" />
                        )}
                      </div>
                      <CreditCard className="h-4 w-4 text-gray-700" />
                      <span className="text-xs font-semibold text-gray-800">Cartão</span>
                    </div>
                    {paymentMethod === 'credit' && (
                      <div className="mt-1.5 pl-6">
                        <img 
                          src="/payment_methods.jpg" 
                          alt="Visa, Master, Elo, Amex, Hiper"
                          className="h-4 w-auto object-contain"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </button>
                  
                  {/* PIX option */}
                  <button
                    onClick={() => setPaymentMethod('pix')}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      paymentMethod === 'pix'
                        ? 'border-green-800 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        paymentMethod === 'pix' ? 'border-green-800' : 'border-gray-300'
                      }`}>
                        {paymentMethod === 'pix' && (
                          <div className="w-2 h-2 rounded-full bg-green-800" />
                        )}
                      </div>
                      <svg viewBox="0 0 512 512" className="h-4 w-4">
                        <path fill="#32BCAD" d="M112.6 311.1l83.1 83.1c14.4 14.4 33.6 22.4 53.9 22.4h.2c20.3 0 39.5-7.9 53.9-22.4l83.1-83.1c14.4-14.4 22.4-33.6 22.4-53.9v-.2c0-20.3-7.9-39.5-22.4-53.9l-83.1-83.1c-14.4-14.4-33.6-22.4-53.9-22.4h-.2c-20.3 0-39.5 7.9-53.9 22.4l-83.1 83.1c-14.4 14.4-22.4 33.6-22.4 53.9v.2c0 20.3 7.9 39.5 22.4 53.9z"/>
                      </svg>
                      <span className="text-xs font-semibold text-gray-800">PIX</span>
                    </div>
                    <p className="text-[10px] text-green-800 font-semibold mt-1 pl-6">5% de desconto</p>
                  </button>
                </div>

                {/* Payment details based on selection */}
                {paymentMethod === 'credit' && (
                  <div className="mt-3 text-center bg-gray-50 rounded-lg p-2.5">
                    <p className="text-xs text-gray-600">
                      até <span className="font-bold text-gray-900">12x</span> de <span className="font-bold text-gray-900">R$ {((finalTotal * 1.2211) / 12).toFixed(2).replace('.', ',')}</span>
                    </p>
                  </div>
                )}
                {paymentMethod === 'pix' && (
                  <div className="mt-3 bg-gradient-to-r from-green-800/10 to-green-900/10 border border-green-200 rounded-lg p-2.5 text-center">
                    <p className="text-green-800 text-lg font-bold">
                      R$ {(finalTotal * 0.95).toFixed(2).replace('.', ',')}
                    </p>
                    <p className="text-[10px] text-green-700">
                      Economize <strong>R$ {pixDiscount.toFixed(2).replace('.', ',')}</strong> (5% off)
                    </p>
                  </div>
                )}
              </div>

              {/* Security message with icon */}
              <div className="flex flex-col items-center gap-1 mt-3">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Shield className="h-4 w-4 text-green-700" />
                  <span>Pagamento 100% seguro via <strong>Mercado Pago</strong></span>
                </div>
                <a href="/sobre#privacidade" className="text-[10px] text-black font-semibold underline hover:no-underline">Como protegemos sua compra?</a>
              </div>

              {/* Trust badges - SELOS DE SEGURANÇA FORTES */}
              <div className="mt-5 pt-5 border-t">
                {/* Selos principais em destaque */}
                <div className="grid grid-cols-2 gap-2 mb-3">
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-2">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Lock className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-green-800">SSL 256-bit</p>
                      <p className="text-[8px] text-green-700">Criptografia Ativa</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg p-2">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <Shield className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-blue-800">Compra Protegida</p>
                      <p className="text-[8px] text-blue-600">Garantia de Entrega</p>
                    </div>
                  </div>
                </div>

                {/* Badges menores */}
                <div className="flex flex-wrap justify-center gap-2">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-600">
                    <CheckCircle className="h-3 w-3 text-green-700" />
                    Site Verificado
                  </span>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-full text-xs text-gray-600">
                    <RotateCcw className="h-3 w-3 text-[#0a0a0a]" />
                    Troca grátis até 7 dias
                  </span>
                </div>

                {/* Selo Google Safe Browsing simulado */}
                <div className="mt-3 flex items-center justify-center gap-2 text-[10px] text-gray-500">
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="#4285F4">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                  </svg>
                  <span>Verificado pelo Google Safe Browsing</span>
                </div>
                
                <p className="text-center text-[10px] text-gray-400 mt-3">
                  Seus dados estão protegidos por criptografia bancária de 256 bits
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
